import { Radio } from "lucide-react";
import { LiveUpdateCard } from "@/components/public/LiveUpdateCard";
import { useI18n } from "@/lib/i18n";
import { useEvenementsDirect } from "@/hooks/useEvenementsDirect";
import { adaptEvenementDirect } from "@/lib/adapters";
import { useSeo } from "@/hooks/useSeo";
import type { LiveEvent } from "@/lib/mock-data";

function LiveEventSection({ event }: { event: LiveEvent }) {
  const { t, locale } = useI18n();
  const title = (event.title as Record<string, string>)[locale] ?? (event.title as Record<string, string>).fr ?? "";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 text-xs">
        {!event.closed ? (
          <span className="inline-flex items-center gap-1.5 rounded-sm bg-alert px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-alert-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-alert-foreground animate-pulse" />{" "}
            {t("live.badge")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-sm bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {t("live.closed")}
          </span>
        )}
        <span className="text-muted-foreground">{t("live.updated")}</span>
      </div>

      <h1 className="font-serif text-3xl md:text-5xl font-semibold tracking-tight">{title}</h1>

      <p className="max-w-2xl text-muted-foreground">{t("live.intro")}</p>

      <div className="flex items-center justify-between rounded-md border border-border bg-card p-4">
        <div className="flex items-center gap-3 text-sm">
          <Radio className="h-4 w-4 text-verified" />
          <span className="font-medium">
            {t("live.updatesCount", { count: event.updates.length })}
          </span>
        </div>
        {!event.closed && (
          <button className="rounded-md bg-foreground px-3 py-1.5 text-xs font-semibold text-background hover:opacity-90">
            {t("cta.followLive")}
          </button>
        )}
      </div>

      {event.updates.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("live.noUpdates")}</p>
      ) : (
        <ul className="space-y-8">
          {event.updates.map((u) => (
            <LiveUpdateCard key={u.id} update={u} />
          ))}
        </ul>
      )}
    </div>
  );
}

function LivePage() {
  const { t } = useI18n();
  useSeo({
    title: `${t("nav.live")} — Ramba News`,
    description: t("live.intro"),
    path: "/live",
  });
  const { data: dtos, isLoading, isError } = useEvenementsDirect();

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 text-sm text-muted-foreground">
        {t("common.loading")}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 text-sm text-muted-foreground">
        {t("error.load")}
      </div>
    );
  }

  const events = (dtos ?? []).map(adaptEvenementDirect);
  // Actifs en premier, clôturés ensuite
  const sorted = [...events].sort((a, b) => {
    if (a.closed === b.closed) return 0;
    return a.closed ? 1 : -1;
  });

  if (sorted.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
        <p className="text-sm text-muted-foreground">{t("live.noEvents")}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 md:py-14 space-y-16">
      {sorted.map((event) => (
        <LiveEventSection key={event.id} event={event} />
      ))}
    </div>
  );
}

export default LivePage;
